import { useEffect, useRef, useState } from 'react';
import { initializeGoogleIdentity, loadGoogleIdentityScript } from '../auth/googleIdentity';

declare namespace NodeJS {
  interface ProcessEnv {
    GOOGLE_CLIENT_ID?: string;
  }
}

let processGoogleClientId: string | undefined;
try {
  processGoogleClientId = process.env.GOOGLE_CLIENT_ID;
} catch (error) {
  // process.env is unavailable in an unbundled browser.
}

const rawImportMetaEnv =
  typeof import.meta !== 'undefined' && (import.meta as any)?.env
    ? ((import.meta as any).env as Record<string, string | undefined>)
    : undefined;

const GOOGLE_CLIENT_ID = (
  processGoogleClientId ??
  rawImportMetaEnv?.GOOGLE_CLIENT_ID ??
  ''
).trim();

const CONFIGURATION_ERROR = 'Google sign-in is not configured. Please contact support.';
const LOAD_ERROR = 'Google sign-in is temporarily unavailable. Please try again.';
const ORGANISATION_GUIDANCE =
  'If your organisation blocks Google sign-in, try a personal Google account.';

export type GoogleSignInButtonProps = {
  onCredential: (credential: string, source: 'button' | 'one_tap') => void;
};

export function GoogleSignInButton({ onCredential }: GoogleSignInButtonProps) {
  const [error, setError] = useState<string | null>(
    GOOGLE_CLIENT_ID ? null : CONFIGURATION_ERROR,
  );
  const buttonElementRef = useRef<HTMLDivElement>(null);
  const onCredentialRef = useRef(onCredential);

  useEffect(() => {
    onCredentialRef.current = onCredential;
  }, [onCredential]);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;

    let active = true;
    let cleanupGoogleIdentity: (() => void) | undefined;
    void loadGoogleIdentityScript()
      .then(() => {
        if (!active || !buttonElementRef.current) return;
        cleanupGoogleIdentity = initializeGoogleIdentity({
          clientId: GOOGLE_CLIENT_ID,
          buttonElement: buttonElementRef.current,
          onCredential: (credential, source) => {
            if (active) {
              onCredentialRef.current(credential, source);
            }
          },
          onPromptUnavailable: () => undefined,
        });
      })
      .catch(() => {
        if (active) {
          setError(LOAD_ERROR);
        }
      });

    return () => {
      active = false;
      cleanupGoogleIdentity?.();
    };
  }, []);

  return (
    <div>
      {error ? (
        <div className="alert alert-warning" role="alert">
          {error}
        </div>
      ) : null}
      <div ref={buttonElementRef} className="google-sign-in-button" data-google-sign-in-button />
      <p className="text-center text-muted small mt-3 mb-0">{ORGANISATION_GUIDANCE}</p>
    </div>
  );
}
